import {
  CurecallButton,
  CheckmarkIcon,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { SMSModal } from 'src/components/sms-modal/sms-modal.component'
import type { TPatient } from 'src/types/patient'
import type { TDashboardPatientMedEvent, TDashboardPatientMedEventOverview } from '../dashboard.type'
import { useQueryClient } from '@tanstack/react-query'
import { DashboardService } from 'src/services/dashboard.service'
import { useDashboard } from '../dashboard.provider'
import { useParams } from 'react-router-dom'
import { useNotification } from 'src/providers/notification.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { getErrorMessage } from 'src/utils/http.util'

type TPatientMedEventOverviewHeaderProps = {
  patient: Pick<TPatient, 'id' | 'firstname' | 'lastname'>
  patientMedEvent: TDashboardPatientMedEventOverview
}

const PatientMedEventOverviewHeader = ({ patient, patientMedEvent }: TPatientMedEventOverviewHeaderProps) => {
  const { i18n, t } = useTranslation()
  const locale = i18n.language
  const { projectId } = useParams()
  const projectIdNum = Number(projectId)
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const { getMedEventLabelById } = useCurrentProject()
  const { selectedPatientMedEvent } = useDashboard()
  const selectedPatientMedEventId = Number(selectedPatientMedEvent?.id)
  const selectedPatientId = Number(selectedPatientMedEvent?.Patient?.id)

  const [isSmsModalOpen, setIsSmsModalOpen] = useState<boolean>(false)

  const markPatientMedEventAsReviewed = async () => {
    try {
      const { data: updatedDashboardPatientMedEvent } =
        await DashboardService.updateDashboardPatientMedEventSatus(
          projectIdNum,
          selectedPatientId,
          selectedPatientMedEventId,
          {
            status: patientMedEvent.status === 'REVIEWED' ? 'TO_BE_REVIEWED' : 'REVIEWED',
          },
        )
      if (updatedDashboardPatientMedEvent) {
        queryClient.setQueryData(
          ['dashboard-patient-med-event', selectedPatientMedEventId],
          () => updatedDashboardPatientMedEvent,
        )
        queryClient.setQueryData(['dashboard'], (oldData: any) => {
          if (!oldData?.dashboardPatientMedEvents) return
          const updatedList = oldData.dashboardPatientMedEvents.map(
            (dashboardPatientMedEvent: TDashboardPatientMedEvent) =>
              dashboardPatientMedEvent.id === updatedDashboardPatientMedEvent.patientMedEvent.id
                ? {
                    ...dashboardPatientMedEvent,
                    status: updatedDashboardPatientMedEvent.patientMedEvent.status,
                  }
                : dashboardPatientMedEvent,
          )
          return { ...oldData, dashboardPatientMedEvents: updatedList }
        })
      }
    } catch (e) {
      notify(
        {
          title: t('pages.dashboard.an-error-occured'),
          message: getErrorMessage(e),
          appearance: ECurecallNotificationAppearance.ERROR,
        },
        15000,
      )
    }
  }

  return (
    <>
      <section className="c-text-lg c-font-light c-justify-between c-flex c-flex-wrap-reverse c-gap-4">
        <div className="c-flex c-flex-row c-items-center c-inter-center c-text-base c-gap-4">
          <span>
            {new Date(patientMedEvent.date).toLocaleDateString(locale, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
            {}
          </span>{' '}
          | <span>{getMedEventLabelById(patientMedEvent.MedEventId)}</span>
        </div>
        <div className="c-flex c-gap-4">
          <CurecallButton
            size={ECurecallSize.MEDIUM}
            appearance={ECurecallButtonAppearance['SECONDARY-OUTLINED']}
            onClick={() => {
              setIsSmsModalOpen((prev) => !prev)
            }}
          >
            {t('components.patient-med-event-overview.see-send-sms')}
          </CurecallButton>
          <CurecallButton
            size={ECurecallSize.MEDIUM}
            appearance={ECurecallButtonAppearance.SECONDARY}
            onClick={markPatientMedEventAsReviewed}
            hasIconAtStart={patientMedEvent.status === 'REVIEWED'}
            iconType={<CheckmarkIcon />}
          >
            {patientMedEvent.status === 'REVIEWED'
              ? t('components.patient-med-event-overview.reviewed')
              : t('components.patient-med-event-overview.mark-as-reviewed')}
          </CurecallButton>
        </div>
      </section>
      {isSmsModalOpen && (
        <SMSModal
          isOpen={true}
          patient={patient}
          onToggle={() => {
            setIsSmsModalOpen(false)
          }}
          currentPatientFollowup={patientMedEvent.PatientFollowup}
        />
      )}
    </>
  )
}

export default PatientMedEventOverviewHeader
